import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { Job } from '../types/job';

export const validateEmail = (email: string) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

export const validatePhone = (phone: string) => {
  if (!phone) return false;
  
  const cleanPhone = phone.replace(/\D/g, '');
  
  if (cleanPhone.startsWith('33')) {
    return cleanPhone.length === 11;
  } else if (cleanPhone.startsWith('0')) {
    return cleanPhone.length === 10;
  }
  
  return false;
};

export const validateSiret = (siret: string) => {
  const cleanSiret = siret.replace(/\D/g, '');
  return cleanSiret.length === 14;
};

export const validatePassword = (password: string) => {
  return password.length >= 8 &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /[0-9]/.test(password);
};

export const formatSiret = (siret: string) => {
  const cleanSiret = siret.replace(/\D/g, '');
  return cleanSiret.replace(/(\d{3})(\d{3})(\d{3})(\d{5})/, '$1 $2 $3 $4');
};

export const formatPhone = (phone: string) => {
  const cleanPhone = phone.replace(/\D/g, '');
  
  if (cleanPhone.startsWith('33')) {
    const nationalNumber = cleanPhone.slice(2);
    return nationalNumber.replace(/(\d{2})(?=\d)/g, '$1 ');
  } else if (cleanPhone.startsWith('0')) {
    return cleanPhone.replace(/(\d{2})(?=\d)/g, '$1 ');
  }
  
  return phone;
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function debounce<T extends (...args: any[]) => any>(
  fn: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return function (...args: Parameters<T>) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), wait);
  };
}

export async function retry<T>(
  fn: () => Promise<T>,
  maxAttempts = 3,
  baseDelay = 1000
): Promise<T> {
  let lastError: Error | undefined;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;
      if (attempt === maxAttempts) break;
      await delay(baseDelay * Math.pow(2, attempt - 1));
    }
  }

  throw lastError;
}
function getPeriodLabel(period?: string): string {
  switch (period) {
    case 'daily':
      return '/ jour';
    case 'weekly':
      return '/ semaine';
    case 'monthly':
      return '/ mois';
    case 'annual':
      return '/ an';
    default:
      return '';
  }
}

export function formatSalary(job: Job): string {
  // Skip salary for internships or apprenticeships
  if (job.contract === 'Stage' || job.contract === 'Alternance') {
    return 'Gratification légale';
  }

  // Set default period if none provided
  const period = job.salary_period || (() => {
    switch (job.contract) {
      case 'Freelance':
        return 'daily';
      case 'Intérim':
        return 'daily';
      default:
        return 'monthly';
    }
  })();

  const formatter = new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0
  });

  const periodLabel = getPeriodLabel(period);

  // First try new salary fields
  if (job.salary_type !== undefined) {
    // Handle range salary (salary_type = 1)
    if (job.salary_type === 1 || job.salary_type === 'fourchette') {
      if (job.salary_min > 0 && job.salary_max && job.salary_max > 0) {
        return `${formatter.format(job.salary_min)} - ${formatter.format(job.salary_max)} ${periodLabel}`;
      }
      if (job.salary_min > 0) {
        return `À partir de ${formatter.format(job.salary_min)} ${periodLabel}`;
      }
      if (job.salary_max && job.salary_max > 0) {
        return `Jusqu'à ${formatter.format(job.salary_max)} ${periodLabel}`;
      }
    }

    // Handle fixed salary (salary_type = 0)
    if ((job.salary_type === 0 || job.salary_type === 'fixe') && job.salary_min > 0) {
      return `${formatter.format(job.salary_min)} ${periodLabel}`;
    }
  }

  // Fallback to legacy salary field
  if (job.salary && job.salary > 0) {
    return `${formatter.format(job.salary)} ${periodLabel}`;
  }

  return 'Salaire à négocier';
}
export function formatDate(dateString?: string): string {
  if (!dateString) return '';
  
  const now = new Date();
  const date = new Date(dateString);
  
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const postDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  
  const diffTime = today.getTime() - postDay.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Aujourd\'hui';
  if (diffDays === 1) return 'Hier';
  if (diffDays < 7) return `Il y a ${diffDays} jours`;
  
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
}

export function getRelativeTimeString(date: Date | string): string {
  const compareDate = date instanceof Date ? date : new Date(date);
  const formatter = new Intl.RelativeTimeFormat('fr', { numeric: 'auto' });
  const diff = compareDate.getTime() - Date.now();
  const diffDays = Math.round(diff / (1000 * 60 * 60 * 24));
  
  if (Math.abs(diffDays) < 1) {
    const diffHours = Math.round(diff / (1000 * 60 * 60));
    return formatter.format(diffHours, 'hour');
  }
  
  return formatter.format(diffDays, 'day');
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatPostalCode(code: string | number): string {
  const codeStr = String(code);
  const dept = codeStr.substring(0, 2);
  return `${dept} - ${codeStr}`;
}

export function formatPercent(value: number, decimals = 0): string {
  return `${(value * 100).toFixed(decimals)}%`;
}

export function randomString(length: number): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

export function truncate(str: string, maxLength: number, suffix = '...'): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength - suffix.length) + suffix;
}

export function toTitleCase(str: string): string {
  return str.replace(
    /\w\S*/g,
    txt => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase()
  );
}

export function isDefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function isValidPhoneNumber(phone: string): boolean {
  const phoneRegex = /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/;
  return phoneRegex.test(phone);
}

const WHATSAPP_NUMBER = '+33745800515';

export function getWhatsAppLink(jobTitle: string, jobUrl: string): string {
  const message = `Je souhaite postuler à l'offre "${jobTitle}". Voici le lien : ${jobUrl}`;
  return `https://api.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=${encodeURIComponent(message)}`;
}